import { useRef, type CSSProperties } from 'react'
import { ArrowUpRight } from '@phosphor-icons/react'
import { PageMeta } from '@/app/PageMeta'
import type { PageRoute } from '@/app/routes'
import { PageHeader } from '@/components/page/PageHeader'
import { kapeToolsFaq, kapeToolsFilm, kapeToolsPage, kapeToolsStats, productTabs } from '@/content/kape-tools'
import { useScrollScene } from '@/hooks/use-scroll-scene'
import { kapeToolsJsonLd } from '@/lib/seo'
import { BrowserWindow } from './BrowserWindow'
import { FilmWindow } from './FilmWindow'
import { roomsScene } from './rooms-scene'
import './kape-tools.css'

const pad2 = (n: number) => String(n).padStart(2, '0')

/** The head (H1 on the page, H2 as a Home section) with the one call to action under the lede. */
function KapeToolsHeader({ nested }: { nested: boolean }) {
  const c = kapeToolsPage
  return (
    <PageHeader level={nested ? 2 : 1} id={nested ? 'kape-tools-title' : undefined} eyebrow={c.eyebrow} titleThin={c.titleThin} titleBold={c.titleBold} lede={c.lede}>
      <div className="kt-cta">
        <a className="pg-btn" href={c.cta.href} target="_blank" rel="noopener">
          {c.cta.label}
          <ArrowUpRight size={16} weight="bold" aria-hidden />
        </a>
        <p className="kt-foot">{c.footnote}</p>
      </div>
    </PageHeader>
  )
}

/** Film, stats, rooms, questions. Nested = inside a Home section, so every heading steps down one level. */
function KapeToolsBody({ nested }: { nested: boolean }) {
  const roomsRef = useRef<HTMLElement>(null)
  useScrollScene(roomsRef, roomsScene)
  const c = kapeToolsPage
  const H2 = nested ? 'h3' : 'h2'
  const H3 = nested ? 'h4' : 'h3'

  return (
    <>
      <div className="kt-film">
        <FilmWindow film={kapeToolsFilm} />
      </div>

      <dl className="kt-stats">
        {kapeToolsStats.map(([n, unit]) => (
          <div key={unit}>
            <dt>{n}</dt>
            <dd className="pg-mono">{unit}</dd>
          </div>
        ))}
      </dl>

      <section ref={roomsRef} className="kt-rooms" style={{ '--n': productTabs.length } as CSSProperties} aria-labelledby="kt-rooms-title">
        <div className="kt-rooms__pin">
          <div className="kt-rooms__nav">
            <H2 id="kt-rooms-title" className="pg-h2">
              {c.roomsTitle}
            </H2>
            <ol className="kt-rooms__list">
              {productTabs.map((room, i) => (
                <li key={room.id}>
                  <button type="button" data-kt-room={i} aria-controls={`kt-room-${room.id}`}>
                    <small>{pad2(i + 1)}</small>
                    {room.label}
                  </button>
                </li>
              ))}
            </ol>
            <span className="kt-rooms__rule" aria-hidden>
              <i data-kt-fill />
            </span>
          </div>

          <div className="kt-rooms__panes">
            {productTabs.map((room, i) => (
              <figure key={room.id} id={`kt-room-${room.id}`} className="kt-room" data-kt-pane={i} data-on={i === 0 ? '' : undefined}>
                <BrowserWindow url={`kape.tools / ${room.label.toLowerCase()}`}>
                  <div className="kt-screen">
                    <img
                      src={room.image.src}
                      srcSet={room.image.srcSet}
                      sizes="auto, (min-width: 1024px) 52vw, 92vw"
                      alt={room.image.alt}
                      width={room.image.width}
                      height={room.image.height}
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                </BrowserWindow>
                <figcaption>
                  <span className="pg-mono">
                    {pad2(i + 1)} / {pad2(productTabs.length)}
                  </span>
                  <H3>{room.label}</H3>
                  <p>{room.body}</p>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="kt-faq" aria-labelledby="kt-faq-title">
        <H2 id="kt-faq-title" className="pg-h2">
          Questions
        </H2>
        <dl>
          {kapeToolsFaq.map((f) => (
            <div key={f.question}>
              <dt>{f.question}</dt>
              <dd>{f.answer}</dd>
            </div>
          ))}
        </dl>
      </section>
    </>
  )
}

/**
 * Kape.Tools (pick A "The film stage" on PLAN-kape-proof.html): the product film under the headline,
 * a quiet stats strip, then the five rooms walked by the page scroll, then three questions answered
 * from the product's own FAQ. Every caption is real text; the screens only illustrate it.
 */
export function KapeToolsPage({ page }: { page: PageRoute }) {
  const c = kapeToolsPage

  return (
    <div className="pg kt">
      <PageMeta page={page} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: kapeToolsJsonLd({ path: page.path, name: page.label, description: c.lede, url: c.cta.href, rooms: productTabs, film: kapeToolsFilm, faq: kapeToolsFaq }),
        }}
      />
      <KapeToolsHeader nested={false} />
      <KapeToolsBody nested={false} />
    </div>
  )
}

/** The same page as the Home section after Proof (the sidebar Kape.Tools entry scrolls Home to #kape-tools). */
export function KapeToolsSection() {
  return (
    <section id="kape-tools" className="pg pg--section kt" aria-labelledby="kape-tools-title">
      <KapeToolsHeader nested />
      <KapeToolsBody nested />
    </section>
  )
}
